import { client } from "../api/client.js";
import { BLOG_TEMPLATES } from "./create-blog-templates.js";
import { formatDateTimeMDY12h } from "../utils/date.js";
import { escapeHtml } from "../ui/escapeHtml.js";
import { openLandingPreviewModal } from "../ui/landingBlogPreview.js";
import { detectCta } from "../utils/ctaDetector.js";

/**
 * Phase 37 Commit 7 — inline badge that surfaces the blog post's
 * faceless-video derivative. Three states:
 *   - generating: include_video was just submitted, render is in flight
 *   - present:    one or more derivatives exist
 *   - hidden:     none of the above
 */
function renderBlogVideoBadge(page) {
    if (page.blogVideoGenerating) {
        return `
          <div class="blog-video-badge blog-video-badge--generating" data-testid="blog-video-badge">
            <span class="ml-ai-spinner" aria-hidden="true"></span>
            <span>Faceless video rendering in the background — check Library in ~60–90s.</span>
          </div>
        `;
    }
    const derivs = Array.isArray(page.blogVideoDerivatives) ? page.blogVideoDerivatives : [];
    if (derivs.length === 0) return '';
    const first = derivs[0];
    return `
      <div class="blog-video-badge blog-video-badge--ready" data-testid="blog-video-badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
        <a href="${first.url ? `#library?focus=${encodeURIComponent(first.derivativeId)}` : '#library'}" data-testid="blog-video-badge-link">
          Video version ready${derivs.length > 1 ? ` (${derivs.length})` : ''}
        </a>
      </div>
    `;
}

function safeJsonParseFE(val) {
    if (Array.isArray(val)) return val;
    if (!val) return [];
    try {
        const parsed = JSON.parse(val);
        // Always return an array — callers use .map()/.includes() and break
        // on null/objects/numbers.
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
}

export function renderBlogWorkbench(page) {
    return `
      <div class="create-blog-workbench" data-testid="blog-editor-root">
        <!-- 2-Column layout: wide editor + actions -->
        <main class="blog-main-editor">
          ${renderBlogEditor(page)}
        </main>

        <aside class="blog-sidebar-right" data-testid="blog-actions-panel">
          ${renderBlogActions(page)}
        </aside>
      </div>
    `;
}

export function renderBlogEditor(page) {
    const post = page.activeBlogPost || { title: '', content_html: '' };
    const isAssisted = page.blogMode === 'ai-assisted';

    return `
      <div class="blog-card blog-editor-card">
        <div class="blog-editor-header">
          <div class="blog-editor-header-top">
            <div class="blog-editor-header-left">
              <span class="blog-editor-label">Blog editor</span>
              <span class="blog-status-badge ${page.blogDirty ? 'blog-dirty-indicator' : ''}">${page.blogDirty ? 'Unsaved' : 'Draft'}</span>
              ${page.blogSavedAt ? `<span class="blog-saved-time">Saved ${formatDateTimeMDY12h(new Date(page.blogSavedAt))}</span>` : ''}
            </div>
            <button class="strip-btn" data-testid="blog-drafts-toggle"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> Blog drafts</button>
          </div>
        </div>

        <!-- Phase 31H: Title input hoisted ABOVE the toolbar so writers name
             their piece before touching rich-text formatting. Same class + testid
             as before so existing handlers and any selector-based test coverage keep working. -->
        <input type="text" class="blog-title-input blog-title-input--top" placeholder="Post Title..." value="${escapeHtml(post.title)}" data-testid="blog-title-input" />

        <div class="blog-toolbar-container">
          <div class="blog-editor-view-toggle">
            <button class="${page.blogEditorView === 'visual' ? 'active' : ''}" data-view="visual" data-testid="blog-editor-visual">Visual</button>
            <button class="${page.blogEditorView === 'text' ? 'active' : ''}" data-view="text" data-testid="blog-editor-text">HTML</button>
          </div>

          <div class="blog-toolbar blog-toolbar--premium">
            <!-- Row 1: Font & Text -->
            <div class="toolbar-row">
              <select class="tb-select tb-select--format" data-cmd="formatBlock" data-testid="blog-toolbar-format" aria-label="Format">
                <option value="p">Paragraph</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
                <option value="h4">Heading 4</option>
                <option value="h5">Heading 5</option>
                <option value="h6">Heading 6</option>
                <option value="pre">Code</option>
              </select>
              <div class="tb-sep"></div>
              <select class="tb-select tb-select--font" data-cmd="fontName" aria-label="Font Family">
                <option value="system-ui, sans-serif">System UI</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="Calibri, sans-serif">Calibri</option>
                <option value="Helvetica, sans-serif">Helvetica</option>
                <option value="Segoe UI, sans-serif">Segoe UI</option>
                <option value="Verdana, sans-serif">Verdana</option>
                <option value="Tahoma, sans-serif">Tahoma</option>
                <option value="Trebuchet MS, sans-serif">Trebuchet MS</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="Garamond, serif">Garamond</option>
                <option value="Palatino, serif">Palatino</option>
                <option value="Times New Roman, serif">Times New Roman</option>
                <option value="Courier New, monospace">Courier New</option>
                <option value="Consolas, monospace">Consolas</option>
                <option value="Impact, sans-serif">Impact</option>
              </select>
              <select class="tb-select tb-select--size" data-cmd="fontSize" aria-label="Font Size">
                <option value="1">8</option>
                <option value="1">9</option>
                <option value="2">10</option>
                <option value="2">11</option>
                <option value="3">12</option>
                <option value="3" selected>14</option>
                <option value="4">16</option>
                <option value="4">18</option>
                <option value="5">20</option>
                <option value="5">24</option>
                <option value="6">28</option>
                <option value="6">32</option>
                <option value="6">36</option>
                <option value="7">48</option>
                <option value="7">64</option>
                <option value="7">72</option>
              </select>
              <div class="tb-sep"></div>
              <button title="Bold (Ctrl+B)" data-cmd="bold" data-testid="blog-toolbar-bold" data-icon="bold" aria-label="Bold"></button>
              <button title="Italic (Ctrl+I)" data-cmd="italic" data-testid="blog-toolbar-italic" data-icon="italic" aria-label="Italic"></button>
              <button title="Underline (Ctrl+U)" data-cmd="underline" data-testid="blog-toolbar-underline" data-icon="underline" aria-label="Underline"></button>
              <button title="Strikethrough" data-cmd="strikeThrough" data-testid="blog-toolbar-strike" data-icon="strike" aria-label="Strikethrough"></button>
              <button title="Subscript" data-cmd="subscript" data-icon="subscript" aria-label="Subscript"></button>
              <button title="Superscript" data-cmd="superscript" data-icon="superscript" aria-label="Superscript"></button>
              <div class="tb-sep"></div>
              <div class="tb-color-wrap" title="Text Color">
                <span class="tb-color-icon" data-role="text-color">A</span>
                <input type="color" class="tb-color-input" data-cmd="foreColor" value="${page.blogTextColor}" data-testid="blog-toolbar-color" aria-label="Text color" />
              </div>
              <div class="tb-color-wrap" title="Highlight Color">
                <span class="tb-color-icon tb-color-icon--highlight" data-role="highlight-color">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 11-6 6v3h9l3-3"/><path d="m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4"/></svg>
                </span>
                <input type="color" class="tb-color-input" data-cmd="hiliteColor" value="#ffff00" aria-label="Highlight" />
              </div>
              <div class="tb-sep"></div>
              <button title="Align Left" data-cmd="justifyLeft" data-testid="blog-toolbar-left" data-icon="left" aria-label="Align Left"></button>
              <button title="Align Center" data-cmd="justifyCenter" data-testid="blog-toolbar-center" data-icon="center" aria-label="Align Center"></button>
              <button title="Align Right" data-cmd="justifyRight" data-testid="blog-toolbar-right" data-icon="right" aria-label="Align Right"></button>
              <button title="Justify" data-cmd="justifyFull" data-testid="blog-toolbar-justify" data-icon="justify" aria-label="Justify"></button>
            </div>
            <!-- Row 2: Structure & Insert -->
            <div class="toolbar-row">
              <button title="Bulleted List" data-cmd="insertUnorderedList" data-testid="blog-toolbar-ul" data-icon="ul" aria-label="Bulleted list"></button>
              <button title="Numbered List" data-cmd="insertOrderedList" data-testid="blog-toolbar-ol" data-icon="ol" aria-label="Numbered list"></button>
              <button title="Decrease Indent" data-cmd="outdent" data-testid="blog-toolbar-outdent" data-icon="outdent" aria-label="Outdent"></button>
              <button title="Increase Indent" data-cmd="indent" data-testid="blog-toolbar-indent" data-icon="indent" aria-label="Indent"></button>
              <div class="tb-sep"></div>
              <button title="Blockquote" data-cmd="formatBlock" data-val="blockquote" data-testid="blog-toolbar-quote" data-icon="quote" aria-label="Blockquote"></button>
              <button title="Code Block" data-cmd="insertCodeBlock" data-icon="code" aria-label="Code block"></button>
              <button title="Horizontal Rule" data-cmd="insertHorizontalRule" data-testid="blog-toolbar-hr" data-icon="hr" aria-label="Horizontal line"></button>
              <button title="Insert Table" data-cmd="insertTable" data-icon="table" aria-label="Insert table"></button>
              <button title="Insert Columns" data-cmd="insertColumns" data-icon="columns" aria-label="Insert columns"></button>
              <div class="tb-sep"></div>
              <button title="Insert Link (Ctrl+K)" data-cmd="createLink" data-testid="blog-toolbar-link" data-icon="link" aria-label="Link"></button>
              <button title="Remove Link" data-cmd="unlink" data-testid="blog-toolbar-unlink" data-icon="unlink" aria-label="Unlink"></button>
              <select class="tb-select tb-select--chars" data-cmd="insertChar" data-testid="blog-toolbar-chars" aria-label="Special characters">
                <option value="">Ω</option>
                <option value="©">©</option><option value="®">®</option><option value="™">™</option>
                <option value="•">•</option><option value="–">–</option><option value="—">—</option>
                <option value="…">…</option><option value="→">→</option><option value="←">←</option>
                <option value="✓">✓</option><option value="★">★</option>
              </select>
              <button title="Read More Tag" data-cmd="insertReadMore" data-testid="blog-toolbar-more" data-icon="more" aria-label="Read More"></button>
              <div class="tb-sep"></div>
              <button title="Insert Image" class="tb-btn--accent" data-cmd="openMediaPanel" data-icon="image" aria-label="Add image"></button>
              <div class="tb-sep"></div>
              <button title="Undo (Ctrl+Z)" data-cmd="undo" data-testid="blog-toolbar-undo" data-icon="undo" aria-label="Undo"></button>
              <button title="Redo (Ctrl+Y)" data-cmd="redo" data-testid="blog-toolbar-redo" data-icon="redo" aria-label="Redo"></button>
              <button title="Clear Formatting" data-cmd="removeFormat" data-testid="blog-toolbar-clear" data-icon="clear" aria-label="Clear formatting"></button>
              <button title="Format Painter" data-cmd="formatPainter" data-icon="painter" aria-label="Format painter"></button>
              <button title="Editing Help" class="blog-toolbar-help" data-testid="blog-toolbar-help" data-icon="help" aria-label="Help editing"></button>
            </div>

            <!-- Media Panel (hidden by default) -->
            <div class="blog-media-panel" data-testid="blog-media-panel" style="display:none;">
              <div class="media-panel-header">
                <h4 class="media-panel-title">Insert Media</h4>
                <button class="media-panel-close" aria-label="Close">&times;</button>
              </div>
              <div class="media-panel-tabs">
                <button class="media-tab active" data-media-tab="upload"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Upload</button>
                <button class="media-tab" data-media-tab="stock"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg> Stock Photos</button>
                <button class="media-tab" data-media-tab="stock-videos"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg> Stock Videos</button>
                <button class="media-tab" data-media-tab="url"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> URL</button>
                <button class="media-tab" data-media-tab="recent"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Recent</button>
              </div>
              <div class="media-panel-body">
                <div class="media-tab-content active" data-tab="upload">
                  <div class="media-dropzone" data-testid="media-dropzone">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    <p class="media-dropzone-text">Drop image here or <button class="media-browse-link" data-testid="media-browse-btn">browse</button></p>
                    <span class="media-dropzone-hint">JPG, PNG, GIF, WebP — up to 10 MB</span>
                  </div>
                </div>
                <div class="media-tab-content" data-tab="stock">
                  <div class="media-stock-search">
                    <input type="text" class="media-stock-input" placeholder="Search free photos..." data-testid="media-stock-search" />
                    <button class="btn btn-sm btn-primary media-stock-btn" data-testid="media-stock-btn">Search</button>
                  </div>
                  <div class="media-stock-results" data-testid="media-stock-results"></div>
                </div>
                <div class="media-tab-content" data-tab="stock-videos">
                  <div class="media-stock-search">
                    <input type="text" class="media-video-input" placeholder="Search free videos..." data-testid="media-video-search" />
                    <button class="btn btn-sm btn-primary media-video-btn" data-testid="media-video-btn">Search</button>
                  </div>
                  <div class="media-video-results" data-testid="media-video-results"></div>
                  <p class="text-muted" style="font-size:11px;margin-top:8px;">Videos by <a href="https://www.pexels.com" target="_blank" rel="noopener">Pexels</a></p>
                </div>
                <div class="media-tab-content" data-tab="url">
                  <div class="media-url-form">
                    <label class="media-field-label">Image URL</label>
                    <input type="url" class="media-field-input" placeholder="https://example.com/image.jpg" data-testid="media-url-input" />
                    <label class="media-field-label">Alt text <span class="text-muted">(optional)</span></label>
                    <input type="text" class="media-field-input media-url-alt" placeholder="Describe the image" />
                    <button class="btn btn-sm btn-primary media-url-insert" data-testid="media-url-insert" style="align-self:flex-start;margin-top:4px;">Insert</button>
                  </div>
                </div>
                <div class="media-tab-content" data-tab="recent">
                  <div class="media-recent-grid" data-testid="media-recent-grid">
                    <p class="text-muted media-recent-empty">No recent images yet</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        ${isAssisted ? `
          <div class="blog-section-prompt-wrapper" style="padding: 12px 32px; border-bottom: 1px solid var(--border); background: var(--surface-2);">
            <label style="font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); display: block; margin-bottom: 4px;">Section Prompt</label>
            <textarea class="blog-section-prompt" placeholder="Describe the section you want to generate here..." style="width: 100%; border: 1px solid var(--border); border-radius: 6px; padding: 8px; font-size: 13px; min-height: 60px; resize: vertical;" data-testid="blog-section-prompt">${page.blogSectionPrompt || ''}</textarea>
          </div>
        ` : ''}

        <div class="blog-editor-container" style="resize: vertical; overflow: auto; min-height: 200px;">
          ${page.blogEditorView === 'visual' ? `
            <div class="blog-content-editor" contenteditable="true" data-testid="blog-editor" data-placeholder="Start writing your blog post here. Use the toolbar above for formatting. Click 'Generate Draft' in the actions panel to auto-fill with AI...">${post.content_html || ''}</div>
          ` : `
            <textarea class="blog-html-editor" data-testid="blog-html-editor" spellcheck="false" placeholder="Start writing your blog post here...">${post.content_html || ''}</textarea>
          `}
        </div>

        <div class="blog-editor-footer">
          <div class="blog-footer-left">
            <div class="blog-convert-wrapper">
              <button class="blog-footer-btn blog-convert-btn" data-testid="blog-convert-social"
                ${page.blogDirty || !post.id || String(post.id).startsWith('new-') ? 'disabled' : ''}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                Convert to social
              </button>
              <div class="blog-convert-dropdown" data-testid="blog-convert-dropdown">
                <button class="blog-convert-option" data-convert="single">Single social post</button>
                <button class="blog-convert-option" data-convert="multiple">Multiple social posts</button>
                <div class="blog-convert-sep"></div>
                <button class="blog-convert-option" data-convert="campaign">Create campaign (incl. blog)</button>
              </div>
            </div>
          </div>
          <div class="blog-footer-right">
            <button class="blog-footer-btn blog-landing-preview-btn" data-testid="blog-landing-preview-btn" title="See how this will look on heistbrain.com/blog">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Landing preview
            </button>
            <button class="blog-footer-btn blog-footer-save" data-testid="blog-save-btn">Save draft</button>
            <button class="blog-footer-btn blog-footer-publish" data-testid="blog-footer-publish"
              ${page.blogDirty || !post.title ? 'disabled' : ''}>${(() => {
                const plat = page.blogPublishPlatform || post.publish_platform || 'none';
                if (plat === 'wordpress') return 'Publish to WordPress';
                if (plat === 'website') return 'Publish to Website';
                return 'Publish';
              })()}</button>
          </div>
        </div>
      </div>
      `;
}

function getBlogWordCount(page) {
    const html = page.activeBlogPost?.content_html || '';
    if (!html) return 0;
    const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text ? text.split(' ').length : 0;
}

function checkItem(page, key) {
    const post = page.activeBlogPost;
    switch (key) {
      case 'title': return !!(post?.title?.trim());
      case 'words': return getBlogWordCount(page) >= 300;
      case 'cta': {
        // Semantic detection — looks at the end of the post for an action
        // verb, link, question, you-verb, or trailing emphasis. No longer
        // requires the literal phrase "call to action" or "sign up".
        return detectCta(post?.content_html || '').present;
      }
      case 'saved': return !page.blogDirty && post?.id && !String(post.id).startsWith('new-');
      default: return false;
    }
}

function renderChecklistItem(page, key) {
    const done = checkItem(page, key);
    const labels = { title: 'Has title', words: '~300 words', cta: 'CTA present', saved: 'Draft saved' };
    const counts = key === 'words' ? ` (${getBlogWordCount(page)})` : '';
    return `<li class="${done ? 'done' : ''}">${labels[key]}${counts}</li>`;
}

export function renderBlogActions(page) {
    const isManual = page.blogMode === 'manual';
    const isAssisted = page.blogMode === 'ai-assisted';

    return `
      <div class="blog-card blog-actions-card">
        <div class="blog-actions-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
          <h2 class="blog-section-title" style="margin:0;">Actions</h2>
          <button class="blog-actions-toggle-btn" data-testid="blog-actions-toggle" title="${page.blogActionsCollapsed ? 'Show Actions' : 'Hide Actions'}" style="background:none; border:none; cursor:pointer; font-size:14px; font-weight:600; color:var(--primary); display:flex; align-items:center; gap:4px;">
            ${page.blogActionsCollapsed
              ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>'
              : 'Hide <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>'
            }
          </button>
        </div>

        <div class="blog-field">
          <label>Schedule Date</label>
          <input type="datetime-local" class="blog-schedule-input" data-testid="blog-schedule-input" value="${page.activeBlogPost?.scheduled_at ? new Date(page.activeBlogPost.scheduled_at).toISOString().slice(0, 16) : ''}" />
        </div>

        <div class="blog-field">
          <label>Publish Platform</label>
          <select class="blog-platform-select" data-testid="blog-platform-select">
            <option value="none" ${(page.blogPublishPlatform || page.activeBlogPost?.publish_platform || 'none') === 'none' ? 'selected' : ''}>None (draft only)</option>
            <option value="wordpress" ${(page.blogPublishPlatform || page.activeBlogPost?.publish_platform) === 'wordpress' ? 'selected' : ''} ${!page.wpConnected ? 'disabled' : ''}>WordPress${!page.wpConnected ? ' (not connected)' : ''}</option>
            <option value="website" ${(page.blogPublishPlatform || page.activeBlogPost?.publish_platform) === 'website' ? 'selected' : ''} ${!page.websiteConnected ? 'disabled' : ''}>Website${!page.websiteConnected ? ' (not connected)' : ''}</option>
          </select>
          ${!page.wpConnected && !page.websiteConnected ? '<small style="display:block;color:var(--text-muted);font-size:11px;margin-top:4px">Connect WordPress or Website in <a href="#settings" style="color:var(--primary)">Settings</a> to publish</small>' : ''}
        </div>

        <div class="blog-field">
          <label>Template</label>
          <select class="blog-template-select" ${isManual ? 'disabled' : ''}>
            <option value="none" ${page.blogTemplate === 'none' ? 'selected' : ''}>None</option>
            <option value="how_to_guide" ${page.blogTemplate === 'how_to_guide' ? 'selected' : ''}>How-to Guide</option>
            <option value="listicle" ${page.blogTemplate === 'listicle' ? 'selected' : ''}>Listicle</option>
            <option value="case_study" ${page.blogTemplate === 'case_study' ? 'selected' : ''}>Case Study</option>
            <option value="newsletter" ${page.blogTemplate === 'newsletter' ? 'selected' : ''}>Newsletter</option>
            <option value="opinion" ${page.blogTemplate === 'opinion' ? 'selected' : ''}>Opinion</option>
            <option value="product_review" ${page.blogTemplate === 'product_review' ? 'selected' : ''}>Product Review</option>
            <option value="tutorial" ${page.blogTemplate === 'tutorial' ? 'selected' : ''}>Tutorial</option>
            <option value="comparison" ${page.blogTemplate === 'comparison' ? 'selected' : ''}>Comparison</option>
            <option value="trend_analysis" ${page.blogTemplate === 'trend_analysis' ? 'selected' : ''}>Trend Analysis</option>
            <option value="interview" ${page.blogTemplate === 'interview' ? 'selected' : ''}>Interview</option>
            <option value="faq" ${page.blogTemplate === 'faq' ? 'selected' : ''}>FAQ</option>
            <option value="resource_roundup" ${page.blogTemplate === 'resource_roundup' ? 'selected' : ''}>Resource Roundup</option>
            <option value="data_story" ${page.blogTemplate === 'data_story' ? 'selected' : ''}>Data Story</option>
            <option value="pillar_page" ${page.blogTemplate === 'pillar_page' ? 'selected' : ''}>Pillar Page</option>
            <option value="announcement" ${page.blogTemplate === 'announcement' ? 'selected' : ''}>Announcement</option>
            <option value="guest_post" ${page.blogTemplate === 'guest_post' ? 'selected' : ''}>Guest Post</option>
          </select>
        </div>

        <div class="blog-field">
          <label>Tags</label>
          ${(() => {
            const registry = page.blogTagRegistry || [];
            const regBySlug = {};
            for (const t of registry) regBySlug[t.slug] = t;
            const selected = Array.isArray(page.activeBlogPost?.tags) ? page.activeBlogPost.tags : [];
            const selSet = new Set(selected);
            const meta = (slug) => regBySlug[slug] || { slug, label: slug.split('-').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || slug, color: '#94a3b8' };
            // Union of registry tags + any tag already on the post, registry first.
            const allSlugs = [];
            for (const t of registry) allSlugs.push(t.slug);
            for (const s of selected) if (!allSlugs.includes(s)) allSlugs.push(s);
            const chips = allSlugs.map((slug) => {
              const m = meta(slug);
              const on = selSet.has(slug);
              const style = on
                ? `background:${m.color}26;color:${m.color};border:1px solid ${m.color}66`
                : `background:var(--surface-2);color:var(--text-muted);border:1px solid var(--border)`;
              return `<button type="button" class="blog-tag-chip${on ? ' selected' : ''}" data-tag="${escapeHtml(slug)}" data-testid="blog-tag-chip" style="display:inline-flex;align-items:center;gap:4px;padding:4px 11px;border-radius:999px;font-size:12px;font-weight:600;cursor:pointer;${style}">${on ? '✓ ' : ''}${escapeHtml(m.label)}</button>`;
            }).join('');
            return `
              <div class="blog-tags-picker" data-testid="blog-tags-picker" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">
                ${chips || '<span style="font-size:12px;color:var(--text-muted)">No tags yet — add one below.</span>'}
              </div>
              <div style="display:flex;gap:6px;align-items:center">
                <input type="text" class="blog-tag-add-input" data-testid="blog-tag-add" placeholder="Add a tag (e.g. Strategy)..." style="flex:1;font-size:12px;padding:6px 10px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--text);box-sizing:border-box;" />
                <button type="button" class="blog-tag-add-btn" data-testid="blog-tag-add-btn" style="font-size:11px;padding:6px 12px;border:1px solid var(--primary);border-radius:6px;background:var(--surface-2);color:var(--primary);cursor:pointer;white-space:nowrap;">Add</button>
              </div>`;
          })()}
          <small style="display:block;color:var(--text-muted);font-size:11px;margin-top:4px">Shown as colored, clickable pills on your blog. Click a tag to toggle it; add a new one to create it.</small>
        </div>

        <div class="blog-field">
          <label>Featured Image</label>
          ${page.activeBlogPost?.featured_image_url ? `
            <div class="blog-featured-preview" style="position:relative;margin-bottom:8px;border-radius:8px;overflow:hidden;border:1px solid var(--border);background:var(--surface-2)">
              <img src="${escapeHtml(page.activeBlogPost.featured_image_url)}" alt="Featured image preview" style="width:100%;aspect-ratio:16/9;object-fit:cover;display:block" />
              <button type="button" class="blog-featured-remove" data-testid="blog-featured-remove" title="Remove featured image" style="position:absolute;top:6px;right:6px;width:24px;height:24px;border-radius:50%;border:none;background:rgba(0,0,0,0.7);color:#fff;font-size:16px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0">&times;</button>
            </div>
          ` : `
            <label class="blog-featured-dropzone" for="blog-featured-file" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;padding:20px 12px;border:1px dashed var(--border);border-radius:8px;background:var(--surface-2);cursor:pointer;text-align:center;aspect-ratio:16/9;transition:border-color 0.2s,background 0.2s">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" style="color:var(--text-muted)"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <span style="font-size:12px;font-weight:600;color:var(--text)">Click or drop image</span>
              <span style="font-size:10px;color:var(--text-muted)">JPG, PNG, WebP</span>
            </label>
            <input type="file" id="blog-featured-file" class="blog-featured-file" accept="image/*" style="display:none" />
          `}
          <div style="display:flex;gap:6px;margin-top:6px">
            <button type="button" class="blog-featured-library-btn" data-testid="blog-featured-library" style="flex:1;font-size:11px;padding:6px 10px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--text);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:5px">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              Browse library
            </button>
            <button type="button" class="blog-featured-ai-btn" data-testid="blog-featured-ai" ${page.blogFeaturedAiGenerating ? 'disabled' : ''} style="flex:1;font-size:11px;padding:6px 10px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--primary, #7c5cff);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:5px">
              ${page.blogFeaturedAiGenerating ? `
                <span class="ml-ai-spinner" style="width:10px;height:10px;"></span>
                Generating…
              ` : `
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                Generate with AI
              `}
            </button>
          </div>
          ${page.blogFeaturedAiError ? `<small style="display:block;color:var(--danger,#ef4444);font-size:11px;margin-top:4px" data-testid="blog-featured-ai-error">${escapeHtml(page.blogFeaturedAiError)}</small>` : ''}
          <input type="url" class="blog-featured-url" data-testid="blog-featured-url" placeholder="or paste an image URL" value="${escapeHtml(page.activeBlogPost?.featured_image_url || '')}" style="margin-top:6px;width:100%;font-size:12px;padding:6px 10px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--text);box-sizing:border-box;" />
          <small style="display:block;color:var(--text-muted);font-size:11px;margin-top:4px">Shown on the blog listing card and in social share previews</small>
        </div>

        <div class="blog-field">
          <label>Topic</label>
          <textarea class="blog-topic-input" data-testid="blog-topic-input" placeholder="What is this post about?" ${isManual ? 'disabled' : ''}>${page.topic}</textarea>
        </div>

        <div class="blog-field">
          <label>Context (Optional)</label>
          <textarea class="blog-context-input" data-testid="blog-context-input" placeholder="Any extra instructions?" ${isManual ? 'disabled' : ''}>${page.sessionContext}</textarea>
        </div>

        ${(page.blogPublishPlatform || page.activeBlogPost?.publish_platform) === 'wordpress' && page.wpConnected ? `
        <div class="blog-platform-settings" data-platform-settings="wordpress">
          <h4 style="margin:0 0 10px;font-size:13px;font-weight:600;display:flex;align-items:center;gap:6px;color:var(--plat-blog)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M21.469 6.825c.84 1.537 1.318 3.3 1.318 5.175 0 3.979-2.156 7.456-5.363 9.325l3.295-9.527c.615-1.539.82-2.771.82-3.864 0-.397-.026-.765-.07-1.109m-7.981.105c.647-.034 1.233-.1 1.233-.1.58-.068.512-.921-.068-.889 0 0-1.744.137-2.87.137-1.054 0-2.828-.137-2.828-.137-.58-.032-.648.854-.068.889 0 0 .552.063 1.13.1l1.677 4.605-2.356 7.07-3.923-11.675c.648-.034 1.233-.1 1.233-.1.58-.068.512-.921-.068-.889 0 0-1.744.137-2.87.137-.202 0-.44-.005-.69-.014C5.638 3.616 8.614 1.5 12.072 1.5c2.573 0 4.919.983 6.673 2.593-.043-.003-.084-.01-.128-.01-1.054 0-1.802.921-1.802 1.91 0 .889.512 1.641 1.057 2.531.41.716.889 1.632.889 2.956 0 .922-.354 1.983-.82 3.466L16.682 18.9l-3.19-9.509-.004-.012zM12.072 22.5c-1.726 0-3.358-.417-4.8-1.154l3.401-9.883 3.483 9.547c.023.056.05.108.077.157a10.5 10.5 0 0 1-2.161.333M1.5 12c0-1.608.373-3.128 1.038-4.484l5.718 15.671A10.53 10.53 0 0 1 1.5 12M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0"/></svg>
            WordPress Settings
          </h4>
          <div class="blog-field">
            <label>WP Status</label>
            <select class="blog-wp-status-select" data-testid="blog-wp-status">
              <option value="publish" ${(page.activeBlogPost?.wp_status || 'publish') === 'publish' ? 'selected' : ''}>Publish (Live)</option>
              <option value="draft" ${page.activeBlogPost?.wp_status === 'draft' ? 'selected' : ''}>Draft (WP Admin only)</option>
            </select>
          </div>
          <div class="blog-field">
            <label>Categories</label>
            <div class="blog-wp-categories" id="blog-wp-categories">
              ${(page.wpCategories || []).length === 0
                ? '<span class="text-muted" style="font-size:11px">Loading...</span>'
                : (page.wpCategories || []).map(c => {
                    const checked = (safeJsonParseFE(page.activeBlogPost?.wp_categories) || []).includes(c.id);
                    return `<label class="blog-wp-cat-label" style="display:flex;align-items:center;gap:5px;font-size:12px;cursor:pointer;padding:2px 0;">
                      <input type="checkbox" class="blog-wp-cat-check" data-cat-id="${c.id}" ${checked ? 'checked' : ''} />
                      ${escapeHtml(c.name)}
                    </label>`;
                  }).join('')
              }
            </div>
          </div>
          <div class="blog-field">
            <label>Tags</label>
            <div class="blog-wp-tags" id="blog-wp-tags">
              <div class="blog-wp-tag-pills" id="blog-wp-tag-pills">
                ${(safeJsonParseFE(page.activeBlogPost?.wp_tags) || []).map(id => {
                    const t = (page.wpTags || []).find(t => t.id === id);
                    return t ? `<span class="blog-wp-tag-pill" data-tag-id="${t.id}">${escapeHtml(t.name)} <button class="blog-wp-tag-remove" data-tag-id="${t.id}">&times;</button></span>` : '';
                  }).join('')}
              </div>
              <div style="display:flex;gap:4px;margin-top:4px;">
                <input type="text" class="blog-wp-tag-input" id="blog-wp-tag-input" placeholder="Add tag..." list="blog-wp-tag-datalist" style="flex:1;font-size:12px;padding:4px 8px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--text);" />
                <datalist id="blog-wp-tag-datalist">
                  ${(page.wpTags || []).map(t => `<option value="${escapeHtml(t.name)}" data-id="${t.id}"></option>`).join('')}
                </datalist>
                <button class="blog-wp-tag-add-btn" type="button" style="font-size:11px;padding:4px 8px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--primary);cursor:pointer;">Add</button>
              </div>
            </div>
          </div>
          <div class="blog-field">
            <label>Excerpt <span style="font-size:10px;color:var(--text-muted)">(auto-generated if blank)</span></label>
            <textarea class="blog-excerpt-input" data-testid="blog-excerpt-input" maxlength="300" placeholder="Custom excerpt for WordPress..." style="min-height:60px;font-size:12px;resize:vertical;">${escapeHtml(page.activeBlogPost?.custom_excerpt || '')}</textarea>
          </div>
        </div>
        ` : ''}

        ${(page.blogPublishPlatform || page.activeBlogPost?.publish_platform) === 'website' && page.websiteConnected ? `
        <div class="blog-platform-settings" data-platform-settings="website">
          <h4 style="margin:0 0 10px;font-size:13px;font-weight:600;display:flex;align-items:center;gap:6px;color:var(--plat-website)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            Website Settings
          </h4>
          <div class="blog-field">
            <label>Meta Description <span style="font-size:10px;color:var(--text-muted)">(for SEO)</span></label>
            <textarea class="blog-meta-description-input" data-testid="blog-meta-description" maxlength="160" placeholder="Short description for search engines..." style="min-height:50px;font-size:12px;resize:vertical;">${escapeHtml(page.activeBlogPost?.meta_description || '')}</textarea>
          </div>
        </div>
        ` : ''}

        <label class="blog-include-video-row" data-testid="blog-include-video-label" style="display:flex;align-items:center;gap:6px;margin:6px 0 8px;font-size:12px;color:var(--text-muted);cursor:pointer;">
          <input type="checkbox" data-testid="blog-include-video-check" id="blog-include-video-check" ${page.blogIncludeVideo ? 'checked' : ''} ${isManual ? 'disabled' : ''} />
          <span>Also produce a faceless video version</span>
        </label>
        <div class="blog-buttons">
          <button class="blog-btn blog-btn--primary blog-generate-btn" ${page.blogGenerating || isManual ? 'disabled' : ''} data-testid="blog-generate-btn">
            ${page.blogGenerating ? 'Generating...' : (isAssisted ? 'Generate Section' : 'Generate Draft')}
          </button>
          <div class="blog-actions-divider"></div>
          <button class="blog-btn btn-glass blog-social-gen-btn"
                  ${page.blogDirty || !page.activeBlogPost?.id || String(page.activeBlogPost.id).startsWith('new-') ? 'disabled' : ''}
                  data-testid="blog-generate-social-btn">
            Generate Social Campaign
          </button>
        </div>
        ${renderBlogVideoBadge(page)}

        <div class="create-feedback-row" style="margin-top:8px"><span id="autosave-indicator" class="autosave-indicator"></span></div>

        <div class="blog-field blog-snippets-field" data-testid="blog-snippets-field">
          <label>Blog Snippets</label>
          <div class="blog-snippets-sidebar-list" id="blog-snippets-sidebar-list">
            <div class="bs-loading-note">Loading…</div>
          </div>
          <div class="blog-snippets-actions">
            <button class="blog-snippet-add-btn" data-testid="blog-snippet-new" type="button">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New snippet
            </button>
            <button class="blog-snippet-manage-btn" data-testid="blog-snippet-manage" type="button">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              Manage
            </button>
          </div>
        </div>

        <div class="blog-checklist">
          <h3 class="blog-checklist-title">Readiness checklist</h3>
          ${page.blogBrandScore != null ? `
          <button type="button" class="blog-brand-score" data-testid="blog-brand-score" data-action="open-brand-score-breakdown"
                  title="${page.blogBrandScore >= 100 ? 'Your post is fully aligned with your brand.' : 'Click to see why this post is not 100% aligned and rewrite it with AI.'}">
            <span class="blog-brand-score-label">Brand alignment</span>
            <span class="blog-brand-score-value blog-brand-score--${page.blogBrandScore >= 80 ? 'high' : page.blogBrandScore >= 50 ? 'mid' : 'low'}">${page.blogBrandScore}%</span>
            ${page.blogBrandScore < 100 ? '<span class="blog-brand-score-hint">View &rarr;</span>' : ''}
          </button>
          ` : ''}
          <ul class="checklist-items">
            ${renderChecklistItem(page, 'title')}
            ${renderChecklistItem(page, 'words')}
            ${renderChecklistItem(page, 'cta')}
            ${renderChecklistItem(page, 'saved')}
          </ul>
        </div>
      </div>
      `;
}

export function renderBlogDraftsDrawer(page) {
    const isOpen = page.blogDraftsDrawerOpen;
    return `
      <div class="blog-drafts-overlay ${isOpen ? 'open' : ''}" data-testid="blog-drafts-overlay"></div>
      <div class="blog-drafts-drawer ${isOpen ? 'open' : ''}" data-testid="blog-drafts-drawer">
        <div class="drawer-header">
          <h2 class="drawer-title">Drafts</h2>
          <button class="drawer-close-btn" data-testid="blog-drafts-close">&times;</button>
        </div>

        <div class="drafts-tabs">
          <button class="drafts-tab ${page.draftFilterTab === 'social' ? 'active' : ''}" data-draft-tab="social">Social</button>
          <button class="drafts-tab ${page.draftFilterTab === 'blog' ? 'active' : ''}" data-draft-tab="blog">WordPress</button>
        </div>

        <div class="drawer-actions">
          <button class="blog-btn full-width" data-testid="blog-drafts-new">New Draft +</button>
        </div>

        <div class="blog-drafts-search-wrapper">
          <input type="text" class="blog-drafts-search" placeholder="Search drafts..." value="${page.blogDraftQuery || ''}" data-testid="blog-drafts-search" />
        </div>

        <div class="blog-drafts-list" data-testid="blog-drafts-list">
          ${renderBlogDraftItems(page)}
        </div>
      </div>
    `;
}

export function renderBlogDraftItems(page) {
    const query = page.blogDraftQuery || '';
    const filtered = (page.blogPosts || []).filter(p =>
      p.title.toLowerCase().includes(query) ||
      (p.content_text && p.content_text.toLowerCase().includes(query))
    );

    if (filtered.length === 0) return '<div class="blog-empty-state">No drafts found.</div>';

    return filtered.map(p => `
      <div class="blog-draft-item ${page.activeBlogPost?.id === p.id ? 'active' : ''}" data-id="${p.id || ''}" data-testid="blog-draft-item">
        <div class="draft-title">${escapeHtml(page.truncate(p.title, 40))}${p.publish_platform === 'wordpress' && p.wp_post_id ? ' <span style="color:var(--plat-blog);font-size:10px;font-weight:700;vertical-align:middle;background:rgba(124,58,237,0.15);padding:1px 5px;border-radius:4px">WP</span>' : ''}${p.published_to_site ? ' <span style="color:var(--plat-website);font-size:10px;font-weight:700;vertical-align:middle;background:rgba(16,185,129,0.15);padding:1px 5px;border-radius:4px">WEB</span>' : ''}</div>
        <div class="draft-meta">${p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ''} • ${p.word_count || 0} words</div>
      </div>
    `).join('');
}

export async function handleBlogGenerate(page) {
    const isAssisted = page.blogMode === 'ai-assisted';
    if (isAssisted && !page.blogSectionPrompt) return page.setBlogError("Add a section prompt to generate content.");
    if (!isAssisted && !page.topic) return page.setBlogError("Add a topic to generate a draft.");

    page.blogGenerating = true;
    page.render();
    try {
      // Build the disabled_snippet_ids list from the current post's per-post
      // opt-out state. Manual snippets opted in live on page.manualSnippetIds
      // and are handled server-side via auto_apply=0 + include flag.
      let disabled_snippet_ids = [];
      try {
        const raw = page.activeBlogPost?.disabled_snippet_ids;
        disabled_snippet_ids = Array.isArray(raw) ? raw : JSON.parse(raw || '[]');
      } catch (_) {}

      const wantVideo = !!page.blogIncludeVideo;
      const res = await client.generateBlogDraft({
        mode: page.blogMode,
        template_key: page.blogTemplate,
        topic: page.topic,
        sessionContext: page.sessionContext,
        sectionPrompt: page.blogSectionPrompt, // Pass section prompt for assisted mode
        disabled_snippet_ids,
        include_video: wantVideo,
      });
      if (wantVideo && res?.ok) {
        // Background render kicked off. Surface a small inline badge until
        // the next page-load fetches derivatives via /video-derivatives.
        page.blogVideoGenerating = true;
        setTimeout(() => {
          // Stop spinning after ~120s — assume render is done or failed.
          // A page reload / blog re-load will fetch real derivative state.
          page.blogVideoGenerating = false;
          page.render();
        }, 120000);
      }
      if (res.ok) {
        const draft = res.draft || res;
        if (res.warning) {
          page.setBlogNotice(`⚠️ ${res.warning}`);
        }
        // Capture brand score + breakdown if returned
        if (draft.brandScore != null) {
          page.blogBrandScore = draft.brandScore;
        }
        if (Array.isArray(draft.brandChecks)) {
          page.blogBrandChecks = draft.brandChecks;
        }

        if (isAssisted) {
          const editor = page.container.querySelector('.blog-content-editor');
          if (editor) {
            editor.focus();
            // Wrap in div for clean insertion
            const html = `<div>${draft.content_html}</div>`;
            document.execCommand('insertHTML', false, html);
            page.activeBlogPost.content_html = editor.innerHTML;
          }
          if (!res.warning) page.setBlogNotice("Section generated and inserted.");
        } else {
          page.activeBlogPost = {
            title: draft.title,
            content_html: draft.content_html,
            status: 'draft'
          };
          if (!res.warning) page.setBlogNotice("Draft generated.");
        }
        page.blogDirty = true;
      } else {
        page.setBlogError(res.error || "Generation failed.");
      }
    } catch (err) {
      console.error(err);
      page.setBlogError("Generate failed. Check server connection and try again.");
    } finally {
      page.blogGenerating = false;
      page.render();
    }
}

export async function handleBlogSave(page) {
    // Final sync from DOM before save
    const titleInput = page.container.querySelector('.blog-title-input');
    const editor = page.container.querySelector('.blog-content-editor');

    if (!page.activeBlogPost) {
      page.activeBlogPost = {
        title: '',
        content_html: '',
        template_key: page.blogTemplate,
        mode: page.blogMode
      };
    }

    if (titleInput) page.activeBlogPost.title = titleInput.value;
    if (editor) page.activeBlogPost.content_html = editor.innerHTML;

    // Sync schedule date
    const scheduleInput = page.container.querySelector('.blog-schedule-input');
    if (scheduleInput && scheduleInput.value) {
      page.activeBlogPost.scheduled_at = new Date(scheduleInput.value).toISOString();
    }

    // Sync platform selection
    const platformSel = page.container.querySelector('.blog-platform-select');
    if (platformSel) page.activeBlogPost.publish_platform = platformSel.value;

    // Sync featured image URL from the input (handles paste-without-blur edge case)
    const featuredUrlInp = page.container.querySelector('.blog-featured-url');
    if (featuredUrlInp) page.activeBlogPost.featured_image_url = featuredUrlInp.value.trim();

    // Sync WP fields from DOM
    const wpStatusSel = page.container.querySelector('.blog-wp-status-select');
    if (wpStatusSel) page.activeBlogPost.wp_status = wpStatusSel.value;
    const excerptInp = page.container.querySelector('.blog-excerpt-input');
    if (excerptInp) page.activeBlogPost.custom_excerpt = excerptInp.value;

    // Sync Website fields
    const metaDescInp = page.container.querySelector('.blog-meta-description-input');
    if (metaDescInp) page.activeBlogPost.meta_description = metaDescInp.value;

    if (!page.activeBlogPost.title) {
      return page.setBlogError("Add a title before saving.");
    }
    try {
      const res = await client.saveBlogPost(page.activeBlogPost);
      if (res && res.ok && res.post) {
        const isNew = !page.activeBlogPost.id || String(page.activeBlogPost.id).startsWith('new-');
        res.post.tags = normalizeBlogTags(res.post);
        page.activeBlogPost = res.post;
        page.blogDirty = false;
        page.blogSavedAt = new Date().toISOString();
        // Once the user saves, a brand-alignment undo snapshot is stale.
        page.blogAlignmentUndo = null;
        const staleToast = document.getElementById('blog-alignment-undo-toast');
        if (staleToast) staleToast.remove();

        if (!Array.isArray(page.blogPosts)) page.blogPosts = [];

        if (isNew) {
          page.blogPosts.unshift(res.post);
        } else {
          const idx = page.blogPosts.findIndex(p => p.id === res.post.id);
          if (idx !== -1) {
            page.blogPosts[idx] = res.post;
          } else {
            page.blogPosts.unshift(res.post);
          }
        }

        page.setBlogNotice("Saved.");
        page.render(); // Immediate feedback and sidebar update
      } else {
        throw new Error(res?.error?.message || "Invalid server response");
      }
    } catch (err) {
      console.error('[BLOG_SAVE_ERROR]', err);
      page.setBlogError("Save failed. Check server connection and try again.");
    }
}

export async function handleBlogToSocial(page) {
    if (page.blogDirty) {
      return page.setBlogError("Save your draft before generating a social campaign.");
    }
    if (!page.activeBlogPost?.id || String(page.activeBlogPost.id).startsWith('new-')) {
      return page.setBlogError("Save the draft first.");
    }

    try {
      const activePlatforms = Object.keys(page.platforms).filter(p => page.platforms[p]);
      if (activePlatforms.length === 0) return page.setBlogError("Select at least one social platform first.");

      const res = await client.generateSocialFromBlog({
        blog_post_id: page.activeBlogPost.id,
        platforms: activePlatforms
      });

      if (res && res.ok) {
        page.setBlogNotice("Social drafts created. Switching to Social view...");
        page.activeCreateMode = 'social';
        localStorage.setItem('activeCreateMode', 'social');
        page.items = res.items || [];
        if (page.items.length > 0) {
          page.selectedItemId = page.items[0].id;
          page.activeDraft = null;
        }
        page.render();
      } else {
        page.setBlogError(res?.error?.message || res?.error || "Social campaign generation failed");
      }
    } catch (err) {
      console.error('[BLOG_TO_SOCIAL_ERROR]', err);
      page.setBlogError("Social campaign generation failed");
    }
}

export function handleTemplateChange(page, templateKey) {
    const editor = page.container.querySelector('.blog-content-editor');
    if (!editor) return;

    const editorText = (editor.innerText || "").replace(/\s/g, "");
    const isEditorEmpty = editorText === "" && editor.querySelectorAll('img, iframe').length === 0;

    if (!isEditorEmpty) {
      page.setBlogError("Template insertion requires an empty editor. Clear the editor to apply a template.");
      const select = page.container.querySelector('.blog-template-select');
      if (select) select.value = page.blogTemplate;
      return;
    }

    const template = BLOG_TEMPLATES[templateKey];
    if (!template) return;

    page.blogTemplate = templateKey;
    const titleInput = page.container.querySelector('.blog-title-input');
    if (titleInput && titleInput.value.trim() === "") {
      titleInput.value = template.title;
    }

    editor.innerHTML = template.html;

    if (!page.activeBlogPost) {
      page.activeBlogPost = { title: '', content_html: '', template_key: templateKey, mode: page.blogMode, status: 'draft' };
    }
    page.activeBlogPost.content_html = template.html;
    page.activeBlogPost.title = titleInput ? titleInput.value : template.title;
    page.activeBlogPost.template_key = templateKey;

    page.blogDirty = true;
    page.setBlogNotice("Template applied.");
    page.renderDirtyUI();
}

export async function loadBlogPosts(page) {
    try {
      const res = await client.listBlogPosts();
      if (res && res.ok) {
        page.blogPosts = res.posts || [];
      } else {
        page.blogPosts = [];
      }
    } catch (err) {
      console.error('[LOAD_BLOG_POSTS_ERROR]', err);
      if (!page.blogPosts) page.blogPosts = [];
    }
    // Load the brand's tag registry for the multi-tag picker. Best-effort.
    try {
      const tagRes = await client.listBlogTags();
      page.blogTagRegistry = (tagRes && tagRes.ok && Array.isArray(tagRes.tags)) ? tagRes.tags : [];
    } catch (err) {
      if (!page.blogTagRegistry) page.blogTagRegistry = [];
    }
}

// Coerce a stored tags value (JSON string from the API, real array, or null)
// into a clean array of slugs. Tolerant of legacy single landing_category.
export function normalizeBlogTags(post) {
    let tags = post && post.tags;
    if (typeof tags === 'string') { try { tags = JSON.parse(tags); } catch (_) { tags = []; } }
    if (!Array.isArray(tags)) tags = [];
    if (!tags.length && post && post.landing_category && post.landing_category !== 'none') {
        tags = [post.landing_category];
    }
    return tags.map(slugifyTag).filter(Boolean);
}

export function slugifyTag(text) {
    return String(text || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

export async function loadBlogPostIntoEditor(page, id) {
    if (!id || id === 'undefined') return;
    const apply = (post) => {
      // Normalize tags (API returns the tags column as a JSON string) into an
      // array so the multi-tag picker can toggle membership directly.
      post.tags = normalizeBlogTags(post);
      page.activeBlogPost = post;
      page.blogPublishPlatform = post.publish_platform || 'none';
      page.blogDirty = false;
      page.render();
      // Phase 37 Commit 7 — fetch any faceless-video derivatives.
      // Best-effort; failures are logged but don't block editor load.
      client.getBlogVideoDerivatives(post.id).then(r => {
          if (r?.ok && Array.isArray(r.derivatives)) {
              page.blogVideoDerivatives = r.derivatives;
              // Stop the "generating" spinner once derivatives exist.
              if (r.derivatives.length > 0) page.blogVideoGenerating = false;
              page.render();
          }
      }).catch(() => { /* ignore — badge just stays hidden */ });
    };
    try {
      const res = await client.getBlogPost(id);
      if (res.ok) { apply(res.post); return; }
    } catch (err) {
      // Not in blog_posts — could be a legacy content_items blog draft.
      // Fall through to migration attempt.
      const is404 = /API_ERROR:.*(not found|Post not found|Route not found)/i.test(err?.message || '');
      if (!is404) {
        console.error('[BLOG_LOAD_ERROR]', err);
        return;
      }
    }
    try {
      const mig = await client.migrateLegacyBlog(id);
      if (mig && mig.ok && mig.post) apply(mig.post);
    } catch (err) {
      console.error('[BLOG_LOAD_MIGRATE_ERROR]', err);
    }
}

export function syncBlogEditorContent(page, from, to) {
    if (from === 'visual' && to === 'text') {
      const editor = page.container.querySelector('.blog-content-editor');
      if (editor) {
        if (!page.activeBlogPost) page.activeBlogPost = { title: '', content_html: '' };
        page.activeBlogPost.content_html = editor.innerHTML;
      }
    } else if (from === 'text' && to === 'visual') {
      const textarea = page.container.querySelector('.blog-html-editor');
      if (textarea) {
        if (!page.activeBlogPost) page.activeBlogPost = { title: '', content_html: '' };
        page.activeBlogPost.content_html = textarea.value;
      }
    }
}
